import { useErrorHandlingSubscription } from '@backoffice-frontend/data-fetching'
import { formatApproximateDifferenceInHoursAndMinutes } from '@backoffice-frontend/fleet-maps-common'
import {
  FccAdConnectivityState,
  FccSdsMode,
} from '@backoffice-frontend/graphql/subscription'

import {
  Badge,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'
import { CardRow } from '../CardRow'
import { CardTable } from '../CardTable'
import { CollapsibleCard } from '../CollapsibleCard'
import {
  useFccSelfDrivingSystemCardLocationSubscription,
  useFccSelfDrivingSystemCardTelemetrySubscription,
} from './SelfDrivingSystemCard.hook'
import { useSelfDrivingSystemTranslations } from './useSelfDrivingSystemTranslations'

const textStyles = { minWidth: 60 }

export const SelfDrivingSystemCard = ({
  vehicleId,
  className,
}: {
  vehicleId: string
  className?: string
}) => {
  const { t } = useTranslation()
  const {
    sdsMissionTranslations,
    sdsErrorTranslations,
    sdsErrorTooltipTranslations,
    sdsMissionTooltipTranslations,
  } = useSelfDrivingSystemTranslations()

  const telemetrySubscription = useErrorHandlingSubscription(
    useFccSelfDrivingSystemCardTelemetrySubscription,
    {
      variables: { vehicleId },
      fetchPolicy: 'no-cache',
      onError: e => {
        console.error('Error in subscription', e)
      },
    },
  )
  const locationSubscription = useErrorHandlingSubscription(
    useFccSelfDrivingSystemCardLocationSubscription,
    {
      variables: { vehicleId },
      onError: e => {
        console.error('Error in subscription', e)
      },
    },
  )
  const telemetry = telemetrySubscription.data?.fccVehicleTelemetry
  const location = locationSubscription.data?.fccVehicleLocation
  const missionConnectivityState =
    telemetry?.missionConnectivity?.connectivityState ?? null

  const hasError = !!telemetry?.sdsRideExecutionError

  return (
    <CollapsibleCard title={t('common_SDS_label')} className={className}>
      <CardTable>
        <CardRow
          label={t('adFleetMap_sdsCardSdsConnectivity_label')}
          state={
            missionConnectivityState === FccAdConnectivityState.Connected ? (
              <Text css={textStyles}>{t('common_online_label')}</Text>
            ) : missionConnectivityState ===
              FccAdConnectivityState.Interrupted ? (
              <Badge label={t('common_offline_label')} variant="critical" />
            ) : (
              <Text css={textStyles}>{t('common_notApplicable_label')}</Text>
            )
          }
          updatedAt={
            telemetry?.missionConnectivity?.timestamp
              ? formatApproximateDifferenceInHoursAndMinutes(
                  new Date(telemetry?.missionConnectivity?.timestamp),
                  new Date(),
                )
              : undefined
          }
          isLoading={telemetrySubscription.loading}
        />
        <CardRow
          label={t('adFleetMap_sdsCardDrivingMode_label')}
          state={
            <Text css={textStyles}>
              {telemetry?.sdsMode === FccSdsMode.Engaged
                ? t('adFleetMap_autonomous_label')
                : t('adFleetMap_manual_label')}
            </Text>
          }
          updatedAt={
            telemetry?.sdsModeUpdatedAt
              ? formatApproximateDifferenceInHoursAndMinutes(
                  new Date(telemetry?.sdsModeUpdatedAt),
                  new Date(),
                )
              : undefined
          }
          isLoading={telemetrySubscription.loading}
        />
        <CardRow
          label={t('adFleetMap_sdsCardLastSpeedReceived_label')}
          state={
            <Text css={textStyles}>
              {`${location ? (location.speed * 3.6).toFixed() : 0}  km/h`}
            </Text>
          }
          updatedAt={
            location?.updatedAt
              ? formatApproximateDifferenceInHoursAndMinutes(
                  new Date(location?.updatedAt),
                  new Date(),
                )
              : undefined
          }
          isLoading={locationSubscription.loading}
        />
        <CardRow
          label={t('adFleetMap_sdsCardJourneyState_label')}
          state={
            <Tooltip>
              <TooltipTrigger>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Text css={textStyles}>
                    {sdsMissionTranslations(telemetry?.sdsMission)}
                  </Text>
                </div>
              </TooltipTrigger>
              <TooltipContent css={{ zIndex: '1300' }}>
                {sdsMissionTooltipTranslations(telemetry?.sdsMission)}
              </TooltipContent>
            </Tooltip>
          }
          updatedAt={
            telemetry?.sdsMissionUpdatedAt
              ? formatApproximateDifferenceInHoursAndMinutes(
                  new Date(telemetry?.sdsMissionUpdatedAt),
                  new Date(),
                )
              : undefined
          }
          isLoading={telemetrySubscription.loading}
        />
        {hasError && (
          <CardRow
            label={t('adFleetMap_sdsCardSdsError_label')}
            state={
              <Tooltip>
                <TooltipTrigger>
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Text color="critical" css={textStyles}>
                      {sdsErrorTranslations(
                        telemetry?.sdsRideExecutionError?.type,
                      )}
                    </Text>
                  </div>
                </TooltipTrigger>
                <TooltipContent css={{ zIndex: '1300' }}>
                  {sdsErrorTooltipTranslations(
                    telemetry?.sdsRideExecutionError?.type,
                  )}
                </TooltipContent>
              </Tooltip>
            }
            updatedAt={
              telemetry?.sdsRideExecutionError?.timestamp
                ? formatApproximateDifferenceInHoursAndMinutes(
                    new Date(telemetry?.sdsRideExecutionError?.timestamp),
                    new Date(),
                  )
                : undefined
            }
            isLoading={telemetrySubscription.loading}
          />
        )}
      </CardTable>
    </CollapsibleCard>
  )
}
